import _ from "lodash";
import React, { useState } from "react";
import classes from "./CheckListEditRow.scss";
import TextAreaInput from "UIComponents/TextAreaInput";
import I18nHOC from "UIComponents/I18nHOC";
import { colors, fontStyle } from "Constants";
import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import { DeleteOutlined } from "@toddle-edu/ds-icons";
import classNames from "classnames";

const inputStyle = {
  cell: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: "0 24px 0 0",
    color: colors.textDefault,
    lineHeight: 1.57,
    ...fontStyle.medium,
  },
  disbledCell: {
    color: colors.textDisabled,
    padding: 16,
  },
  textAreaContainerStyle: {
    flexDirection: "row",
  },
};

const CheckListOption = ({
  label,
  writeCheckListElement,
  index,
  id,
  shouldDisable,
  onColumnFocus,
  setFocusedCell,
  setCellRef,
  criteriaId,
  item,
  t,
  onOptionDeleteClick,
  showDeleteButton,
  textAreaStyles,
  optionStyle,
  showHoverState,
  writeChecklistElementLocal,
  assignmentId,
  saveAssessmentToolUpdateStatusInRedux,
}) => {
  const [optionLabel, setOptionLabel] = useState(label);

  const refKey = `Option_${id}_Criteria_${criteriaId}`;

  const optionClass = classNames({
    [classes.option]: true,
    [classes.optionDisabled]: shouldDisable,
    [classes.optionHover]: showHoverState,
  });

  const updateChecklistOption = value => {
    setOptionLabel(value);
    writeCheckListElement({
      id: id,
      label: value,
      type: "CHECKLIST_OPTION",
      elementDetails: item,
    });
    saveAssessmentToolUpdateStatusInRedux({
      assignmentId,
      toolType: "CHECKLIST",
    });
  };

  return (
    <div
      onFocus={() => {
        onColumnFocus ? onColumnFocus(id) : null;
      }}
      className={classes.columnItem}
    >
      <div className={optionClass} style={optionStyle}>
        <TextAreaInput
          disabled={shouldDisable}
          value={optionLabel}
          textAreaStyles={{
            ...inputStyle.cell,
            ...(shouldDisable && inputStyle.disbledCell),
            ...textAreaStyles,
          }}
          updateInputRef={ref => setCellRef({ ref, key: refKey })}
          onBlurInputField={() => {
            setFocusedCell(null);
            writeChecklistElementLocal({
              id: id,
              label: optionLabel,
              type: "CHECKLIST_OPTION",
              elementDetails: item,
            });
          }}
          onFocusInputField={() => setFocusedCell(refKey)}
          placeholder={`${t("common:option")} ${index + 1}`}
          updateInputField={updateChecklistOption}
          className={classes.optionInput}
          minRows={1}
          textAreaContainerStyle={inputStyle.textAreaContainerStyle}
        />
        {!shouldDisable && showDeleteButton && (
          <div className={classes.optionActionButton}>
            <Tooltip
              tooltip={t("common:delete_with_label", {
                label: t("common:option"),
              })}
            >
              <IconButton
                icon={<DeleteOutlined />}
                variant={"plain"}
                size={"x-small"}
                onClick={() => onOptionDeleteClick(id)}
                disabled={_.includes(id, "NEW")}
              />
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};

const CheckListEditRow = props => {
  const {
    id,
    label,
    options,
    index,
    minOptionCount,
    onOptionDeleteClick,
    onItemDeleteClick,
    writeCheckListElement,
    onColumnFocus,
    onRowFocus,
    setCellRef,
    setFocusedCell,
    item,
    t,
    showDeleteCriteriaButton,
    toolStyles = {},
    showHoverState,
    writeChecklistElementLocal,
    assignmentId,
    saveAssessmentToolUpdateStatusInRedux,
  } = props;

  const [itemLabel, setItemLabel] = useState(label);

  const refKey = `Criteria_${id}`;
  const disabled = _.includes(id, "NEW");

  const labelContainerClass = classNames({
    [classes.labelContainer]: true,
    [classes.disabled]: disabled,
  });

  const {
    labelContainerStyle = {},
    textAreaStyles = {},
    rowStyle = {},
    optionContainerStyle = {},
    optionStyle = {},
  } = toolStyles || {};

  const updateChecklistItem = value => {
    setItemLabel(value);
    writeCheckListElement({
      id: id,
      label: value,
      elementDetails: item,
      type: "CHECKLIST_ITEM",
    });
    saveAssessmentToolUpdateStatusInRedux({
      assignmentId,
      toolType: "CHECKLIST",
    });
  };

  return (
    <div className={classes.container} onFocus={() => onRowFocus(id)}>
      <div className={classes.row} style={rowStyle}>
        <div className={labelContainerClass} style={labelContainerStyle}>
          <TextAreaInput
            value={itemLabel}
            textAreaStyles={{
              ...inputStyle.cell,
              ...textAreaStyles,
            }}
            onBlurInputField={() => {
              setFocusedCell(null);
              writeChecklistElementLocal({
                id: id,
                label: itemLabel,
                elementDetails: item,
                type: "CHECKLIST_ITEM",
              });
            }}
            onFocusInputField={() => setFocusedCell(refKey)}
            updateInputRef={ref => setCellRef({ ref, key: refKey })}
            disabled={disabled}
            placeholder={t("classRoom:type_your_criteria_here")}
            updateInputField={updateChecklistItem}
            className={classes.labelInput}
            minRows={1}
            textAreaContainerStyle={inputStyle.textAreaContainerStyle}
          />

          {showDeleteCriteriaButton && (
            <div className={classes.labelActionButton}>
              <Tooltip
                tooltip={t("common:delete_with_label", {
                  label: t("common:criteria"),
                })}
              >
                <IconButton
                  icon={<DeleteOutlined variant="subtle" />}
                  variant={"plain"}
                  size={"x-small"}
                  onClick={() => onItemDeleteClick(item)}
                  disabled={_.includes(item?.id, "NEW")}
                />
              </Tooltip>
            </div>
          )}
        </div>

        <div className={classes.optionContainer} style={optionContainerStyle}>
          {_.map(options, (item, optionIndex) => {
            const shouldDisable = index > 0 || _.includes(item.id, "NEW");
            return (
              <CheckListOption
                setCellRef={setCellRef}
                setFocusedCell={setFocusedCell}
                onColumnFocus={onColumnFocus}
                key={item.id}
                criteriaId={id}
                item={item}
                label={item.label}
                id={item.id}
                optionIndex={optionIndex}
                minOptionCount={minOptionCount}
                onOptionDeleteClick={() => onOptionDeleteClick(item.id)}
                writeCheckListElement={writeCheckListElement}
                index={optionIndex}
                shouldDisable={shouldDisable}
                t={t}
                showDeleteButton={_.size(options) > 1}
                textAreaStyles={textAreaStyles}
                optionStyle={optionStyle}
                showHoverState={showHoverState}
                writeChecklistElementLocal={writeChecklistElementLocal}
                assignmentId={assignmentId}
                saveAssessmentToolUpdateStatusInRedux={
                  saveAssessmentToolUpdateStatusInRedux
                }
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default I18nHOC({ resource: ["unitPlan", "common", "classRoom"] })(
  CheckListEditRow
);
